import type {
  PerformanceDetailZoneParams,
  PerformanceScheduleZoneParams,
  PerformanceSelectionMode,
  ProgrammeDay,
  ProgrammePayload,
  ProgrammePerformance,
  ProgrammeScope,
  ProgrammeSponsor,
  ProgrammeSponsorTier,
  ProgrammeThemeTokens,
  ProgrammeZoneParams,
  SponsorStripZoneParams,
} from './programme-types';

/**
 * Programme selection and formatting for the three programme zones.
 *
 * A deliberate port, not an import: `apps/api/src/performances/programme.ts`
 * holds the same rules over stored rows, and this holds them over the wire
 * payload. The API has no workspace dependencies and this package ships `.vue`
 * files, so nothing is shared as a module today; what keeps the two honest is
 * that both are pure, both take `now` as an argument, and both are tested
 * against the same awkward instants (23:50, 00:30, the two DST days).
 *
 * Everything here is plain arithmetic on instants plus `Intl` for formatting.
 * No timezone maths: the payload already stamps each performance with its
 * programme day and enumerates the day windows (see ./programme-types.ts).
 */

// -----------------------------------------------------------------------------
// Zone parameters
// -----------------------------------------------------------------------------

/** Kept in step with the Zod defaults in apps/api/src/layouts/validation.ts. */
const DETAIL_DEFAULTS = {
  selection: 'current_or_next',
  templateVariant: 'split',
  showCast: false,
  showSurtitles: true,
  showSponsors: false,
  showTicketQr: false,
  showSurtitleQr: false,
} as const;

const SCHEDULE_DEFAULTS = {
  days: 7,
  maxItems: 8,
  groupBy: 'day',
  showLocation: false,
  showSurtitles: true,
  templateVariant: 'list',
} as const;

const SPONSOR_DEFAULTS = {
  source: 'both',
  maxVisible: 4,
  rotateSeconds: 15,
  preferDarkLogo: false,
  templateVariant: 'row',
} as const;

const ALL_TIERS: readonly ProgrammeSponsorTier[] = ['presenting', 'main', 'partner'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function pickEnum<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

function pickBool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function pickInt(value: unknown, fallback: number, min: number, max: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(value)));
}

function pickStrings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
}

function pickTheme(value: unknown): ProgrammeThemeTokens | undefined {
  if (!isRecord(value)) return undefined;
  const theme: ProgrammeThemeTokens = {};
  for (const [token, tokenValue] of Object.entries(value)) {
    if (typeof tokenValue === 'string') theme[token] = tokenValue;
  }
  return Object.keys(theme).length > 0 ? theme : undefined;
}

function scopeFields(raw: Record<string, unknown>): {
  scope: ProgrammeScope;
  locationIds: string[];
  theme?: ProgrammeThemeTokens;
} {
  const theme = pickTheme(raw.theme);
  const base = {
    scope: pickEnum<ProgrammeScope>(raw.scope, ['screen', 'all', 'explicit'], 'screen'),
    locationIds: pickStrings(raw.locationIds),
  };
  // exactOptionalPropertyTypes: an absent theme must be absent, not undefined.
  return theme ? { ...base, theme } : base;
}

/**
 * Normalises a zone's stored `parameters` JSON into programme zone parameters,
 * or `null` when the zone is not a programme zone.
 *
 * The API validates and defaults on write, so this is a reader's safety net for
 * a payload that predates a parameter — an unreadable value falls back to its
 * default rather than blanking a wall.
 */
export function parseProgrammeZoneParams(parameters: unknown): ProgrammeZoneParams | null {
  if (!isRecord(parameters)) return null;
  const kind = parameters.kind;

  if (kind === 'performance-detail') {
    const params: PerformanceDetailZoneParams = {
      kind: 'performance-detail',
      ...scopeFields(parameters),
      selection: pickEnum<PerformanceSelectionMode>(
        parameters.selection,
        ['current_or_next', 'next_any'],
        DETAIL_DEFAULTS.selection,
      ),
      templateVariant: pickEnum(
        parameters.templateVariant,
        ['poster', 'split', 'text'] as const,
        DETAIL_DEFAULTS.templateVariant,
      ),
      showCast: pickBool(parameters.showCast, DETAIL_DEFAULTS.showCast),
      showSurtitles: pickBool(parameters.showSurtitles, DETAIL_DEFAULTS.showSurtitles),
      showSponsors: pickBool(parameters.showSponsors, DETAIL_DEFAULTS.showSponsors),
      showTicketQr: pickBool(parameters.showTicketQr, DETAIL_DEFAULTS.showTicketQr),
      showSurtitleQr: pickBool(parameters.showSurtitleQr, DETAIL_DEFAULTS.showSurtitleQr),
    };
    return params;
  }

  if (kind === 'performance-schedule') {
    const params: PerformanceScheduleZoneParams = {
      kind: 'performance-schedule',
      ...scopeFields(parameters),
      days: pickInt(parameters.days, SCHEDULE_DEFAULTS.days, 1, 31),
      maxItems: pickInt(parameters.maxItems, SCHEDULE_DEFAULTS.maxItems, 1, 40),
      groupBy: pickEnum(parameters.groupBy, ['day', 'none'] as const, SCHEDULE_DEFAULTS.groupBy),
      showLocation: pickBool(parameters.showLocation, SCHEDULE_DEFAULTS.showLocation),
      showSurtitles: pickBool(parameters.showSurtitles, SCHEDULE_DEFAULTS.showSurtitles),
      templateVariant: pickEnum(
        parameters.templateVariant,
        ['list', 'grid', 'compact'] as const,
        SCHEDULE_DEFAULTS.templateVariant,
      ),
    };
    return params;
  }

  if (kind === 'sponsor-strip') {
    const tiers = pickStrings(parameters.tiers).filter((t): t is ProgrammeSponsorTier =>
      (ALL_TIERS as readonly string[]).includes(t),
    );
    const params: SponsorStripZoneParams = {
      kind: 'sponsor-strip',
      ...scopeFields(parameters),
      source: pickEnum(
        parameters.source,
        ['production', 'house', 'both'] as const,
        SPONSOR_DEFAULTS.source,
      ),
      tiers: tiers.length > 0 ? tiers : [...ALL_TIERS],
      maxVisible: pickInt(parameters.maxVisible, SPONSOR_DEFAULTS.maxVisible, 1, 12),
      rotateSeconds: pickInt(parameters.rotateSeconds, SPONSOR_DEFAULTS.rotateSeconds, 3, 600),
      preferDarkLogo: pickBool(parameters.preferDarkLogo, SPONSOR_DEFAULTS.preferDarkLogo),
      templateVariant: pickEnum(
        parameters.templateVariant,
        ['row', 'stack'] as const,
        SPONSOR_DEFAULTS.templateVariant,
      ),
    };
    return params;
  }

  return null;
}

// -----------------------------------------------------------------------------
// Scope
// -----------------------------------------------------------------------------

/**
 * The performances a zone may consider, given its scope.
 *
 * A `screen`-scoped zone on an unbound screen resolves to nothing. Falling back
 * to the whole house would make a mis-provisioned screen look plausibly correct
 * for a season; falling back to empty gets it fixed on day one (D4).
 */
export function scopedPerformances(
  payload: ProgrammePayload,
  params: Pick<ProgrammeZoneParams, 'scope' | 'locationIds'>,
): ProgrammePerformance[] {
  if (params.scope === 'all') return [...payload.performances];
  const allowed = new Set(
    params.scope === 'explicit' ? params.locationIds : payload.screenLocationIds,
  );
  if (allowed.size === 0) return [];
  return payload.performances.filter((p) => allowed.has(p.location.id));
}

/** True when a `screen`-scoped zone has nothing to render because of provisioning. */
export function isUnboundScreenZone(
  payload: ProgrammePayload,
  params: Pick<ProgrammeZoneParams, 'scope'>,
): boolean {
  return params.scope === 'screen' && payload.screenLocationIds.length === 0;
}

// -----------------------------------------------------------------------------
// Time
// -----------------------------------------------------------------------------

const ms = (iso: string): number => Date.parse(iso);

export function isInProgress(perf: ProgrammePerformance, now: Date): boolean {
  return ms(perf.startsAt) <= now.getTime() && now.getTime() < ms(perf.endsAt);
}

/**
 * True while a performance may still be shown: it has not finished, or finished
 * within the grace period. An in-progress one is always retained — a board that
 * goes blank halfway through the show it announces is worse than no board.
 */
export function isStillShowing(perf: ProgrammePerformance, now: Date, graceMs = 0): boolean {
  return now.getTime() < ms(perf.endsAt) + graceMs;
}

/**
 * The programme day containing `now`, or — when the payload's window starts in
 * the future — its first day. `undefined` once the whole window is in the past,
 * which is how a payload cached past its usefulness renders empty rather than
 * showing last week.
 */
export function currentProgrammeDay(
  payload: ProgrammePayload,
  now: Date,
): ProgrammeDay | undefined {
  const t = now.getTime();
  const containing = payload.days.find((d) => ms(d.startsAt) <= t && t < ms(d.endsAt));
  if (containing) return containing;
  return payload.days.find((d) => ms(d.startsAt) > t);
}

function byStart(a: ProgrammePerformance, b: ProgrammePerformance): number {
  const d = ms(a.startsAt) - ms(b.startsAt);
  return d !== 0 ? d : a.id.localeCompare(b.id);
}

/**
 * The single performance a `performance-detail` zone renders.
 *
 * `current_or_next` is what a stage-door panel wants: what is running, else the
 * next one *today*, else nothing — deliberately not tomorrow's matinee, which
 * reads as tonight's performance to anyone glancing at it. `next_any` is what a
 * foyer teaser wants and is strictly forward-looking.
 *
 * Cancelled and postponed performances stay in the running: the one evening the
 * audience most needs to be told something is not the evening to show next week
 * instead.
 */
export function selectPerformance(
  performances: readonly ProgrammePerformance[],
  payload: ProgrammePayload,
  now: Date,
  mode: PerformanceSelectionMode = 'current_or_next',
): ProgrammePerformance | undefined {
  const ordered = [...performances].sort(byStart);
  if (mode === 'next_any') {
    return ordered.find((p) => ms(p.startsAt) > now.getTime());
  }
  const today = currentProgrammeDay(payload, now);
  if (!today) return undefined;
  const from = ms(today.startsAt);
  const to = ms(today.endsAt);
  return ordered.find(
    (p) => isStillShowing(p, now, payload.graceMs) && ms(p.startsAt) >= from && ms(p.startsAt) < to,
  );
}

export interface SelectedProgrammeList {
  items: ProgrammePerformance[];
  /** Day key of the programme day containing `now`, for "Today" headings. */
  todayKey: string | undefined;
  /** Items dropped by `maxItems`, so a template can say "+3 more". */
  truncated: number;
}

/**
 * The performances a `performance-schedule` zone lists: a rolling range forward
 * from the current programme day, never a calendar week — a "this week" board
 * rendered on a Saturday showing two remaining days is a broken board (D12).
 */
export function selectPerformanceList(
  performances: readonly ProgrammePerformance[],
  payload: ProgrammePayload,
  now: Date,
  selection: { days: number; maxItems?: number },
): SelectedProgrammeList {
  const today = currentProgrammeDay(payload, now);
  if (!today) return { items: [], todayKey: undefined, truncated: 0 };

  const firstIndex = payload.days.indexOf(today);
  const lastIndex = Math.min(payload.days.length - 1, firstIndex + selection.days - 1);
  const last = payload.days[lastIndex] ?? today;
  const from = ms(today.startsAt);
  const to = ms(last.endsAt);

  const inRange = performances
    .filter(
      (p) =>
        ms(p.startsAt) >= from && ms(p.startsAt) < to && isStillShowing(p, now, payload.graceMs),
    )
    .sort(byStart);

  const limit = selection.maxItems ?? inRange.length;
  return {
    items: inRange.slice(0, limit),
    todayKey: today.dayKey,
    truncated: Math.max(0, inRange.length - limit),
  };
}

export interface ProgrammeDayGroup {
  dayKey: string;
  isToday: boolean;
  items: ProgrammePerformance[];
}

/** Groups a selected list by programme day, preserving list order. */
export function groupByProgrammeDay(
  items: readonly ProgrammePerformance[],
  todayKey: string | undefined,
): ProgrammeDayGroup[] {
  const groups: ProgrammeDayGroup[] = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    if (last && last.dayKey === item.programmeDay) {
      last.items.push(item);
      continue;
    }
    groups.push({
      dayKey: item.programmeDay,
      isToday: item.programmeDay === todayKey,
      items: [item],
    });
  }
  return groups;
}

// -----------------------------------------------------------------------------
// Sponsors
// -----------------------------------------------------------------------------

/**
 * Sponsors for a `sponsor-strip` zone: the resolved production's, the house set,
 * or both merged. Production sponsors win a duplicate id, because a house
 * sponsor that also backs this production is contracted at the production's
 * tier for the evening — merging by sponsor id is the whole reason a house
 * sponsor carries an operator-set id rather than a row id.
 */
export function sponsorsForZone(
  params: Pick<SponsorStripZoneParams, 'source' | 'tiers'>,
  payload: ProgrammePayload,
  performance: ProgrammePerformance | undefined,
): ProgrammeSponsor[] {
  const production = performance?.production.sponsors ?? [];
  const merged = new Map<string, ProgrammeSponsor>();
  if (params.source === 'house' || params.source === 'both') {
    for (const s of payload.houseSponsors) merged.set(s.id, s);
  }
  if (params.source === 'production' || params.source === 'both') {
    for (const s of production) merged.set(s.id, s);
  }
  const allowed = new Set(params.tiers);
  const rank = (tier: ProgrammeSponsorTier): number => ALL_TIERS.indexOf(tier);
  return [...merged.values()]
    .filter((s) => allowed.has(s.tier))
    .sort((a, b) => rank(a.tier) - rank(b.tier) || a.name.localeCompare(b.name));
}

/**
 * Appearances per rotation cycle, by tier. A presenting sponsor is on screen
 * three times as often as a partner, which is what the tiers are sold as.
 */
const TIER_APPEARANCES: Record<ProgrammeSponsorTier, number> = {
  presenting: 3,
  main: 2,
  partner: 1,
};

/**
 * The pages a strip rotates through, weighted by tier.
 *
 * The cycle is built in rounds: round one carries every sponsor, round two only
 * those with at least two appearances, round three only the presenting tier. So
 * a sponsor's appearances are spread across the cycle instead of clustering, and
 * every sponsor — including the smallest partner — is on screen at least once
 * per cycle. Weighting by *appearances per cycle* rather than by dwell time is
 * why this is best-effort: with four sponsors and room for three, the ratios
 * land where the page boundaries let them.
 *
 * Two shaping rules, both about what a wall looks like rather than about maths:
 * no sponsor appears twice on one page (a doubled logo reads as a bug), and the
 * last page is padded from the top of the tier order so the strip never turns to
 * a half-empty page. When everyone fits at once there is nothing to rotate and
 * the result is a single page.
 */
export function sponsorPages(
  sponsors: readonly ProgrammeSponsor[],
  maxVisible: number,
): ProgrammeSponsor[][] {
  if (sponsors.length === 0) return [];
  const size = Math.max(1, maxVisible);
  if (sponsors.length <= size) return [[...sponsors]];

  const rounds = Math.max(...sponsors.map((s) => TIER_APPEARANCES[s.tier]));
  const sequence: ProgrammeSponsor[] = [];
  for (let round = 1; round <= rounds; round += 1) {
    for (const sponsor of sponsors) {
      if (TIER_APPEARANCES[sponsor.tier] >= round) sequence.push(sponsor);
    }
  }

  const pages: ProgrammeSponsor[][] = [];
  const queue = [...sequence];
  while (queue.length > 0) {
    const page: ProgrammeSponsor[] = [];
    while (page.length < size && queue.length > 0) {
      // The first candidate not already on this page; a repeat is deferred to
      // the next page rather than dropped, so appearance counts survive.
      const index = queue.findIndex((s) => !page.some((seen) => seen.id === s.id));
      if (index === -1) break;
      page.push(...queue.splice(index, 1));
    }
    for (const sponsor of sponsors) {
      if (page.length >= size) break;
      if (!page.some((seen) => seen.id === sponsor.id)) page.push(sponsor);
    }
    pages.push(page);
  }
  return pages;
}

/** The logo URL to load, honouring the zone's dark-variant preference. */
export function sponsorLogoUrl(sponsor: ProgrammeSponsor, preferDark: boolean): string | undefined {
  return preferDark ? (sponsor.logoDarkUrl ?? sponsor.logoUrl) : sponsor.logoUrl;
}

// -----------------------------------------------------------------------------
// Chrome strings
//
// Day names, relative labels and status wording are the *player's* language,
// selected by the screen's locale, independent of the language of the imported
// programme (D13). The player's i18n bundle supplies these; the English
// defaults below keep the components renderable on their own.
// -----------------------------------------------------------------------------

export interface ProgrammeStrings {
  today: string;
  tomorrow: string;
  inProgress: string;
  cancelled: string;
  postponed: string;
  soldOut: string;
  doorsOpen: string;
  /** `{minutes}` */
  runtime: string;
  /** `{count}` */
  intermissions: string;
  /** `{languages}`. Used where the languages read as a sentence. */
  surtitles: string;
  /** Heading for a row of per-language badges. */
  surtitlesLabel: string;
  /** Caption under the ticket QR code. */
  ticketsQr: string;
  /** Caption under the surtitle-app QR code. */
  surtitleAppQr: string;
  noPerformance: string;
  noPerformances: string;
  /** `{count}` */
  more: string;
  /** Force a clock convention; omitted means "whatever the locale does". */
  timeFormat?: 'h12' | 'h23';
}

export const DEFAULT_PROGRAMME_STRINGS: ProgrammeStrings = {
  today: 'Today',
  tomorrow: 'Tomorrow',
  inProgress: 'Now playing',
  cancelled: 'Cancelled',
  postponed: 'Postponed',
  soldOut: 'Sold out',
  doorsOpen: 'Doors',
  runtime: '{minutes} min',
  intermissions: '{count} intermissions',
  surtitles: 'Surtitles: {languages}',
  surtitlesLabel: 'Surtitles',
  ticketsQr: 'Tickets',
  surtitleAppQr: 'Surtitle app',
  noPerformance: 'No performance',
  noPerformances: 'No performances',
  more: '+{count} more',
};

/**
 * German chrome. Lives here rather than in an app's i18n bundle because both
 * consumers — the player, which has no i18n framework, and the admin preview,
 * which has its own — must render the *same* wording for the preview to be
 * WYSIWYG.
 */
const DE_PROGRAMME_STRINGS: ProgrammeStrings = {
  today: 'Heute',
  tomorrow: 'Morgen',
  inProgress: 'Läuft',
  cancelled: 'Abgesagt',
  postponed: 'Verlegt',
  soldOut: 'Ausverkauft',
  doorsOpen: 'Einlass',
  runtime: '{minutes} Min.',
  intermissions: '{count} Pausen',
  surtitles: 'Übertitel: {languages}',
  surtitlesLabel: 'Übertitel',
  ticketsQr: 'Tickets',
  surtitleAppQr: 'Übertitel-App',
  noPerformance: 'Keine Vorstellung',
  noPerformances: 'Keine Vorstellungen',
  more: '+{count} weitere',
  timeFormat: 'h23',
};

const PROGRAMME_STRINGS: Record<string, ProgrammeStrings> = {
  en: DEFAULT_PROGRAMME_STRINGS,
  de: DE_PROGRAMME_STRINGS,
};

/**
 * Chrome strings for a locale, falling back through the base language and then
 * to English. `de-AT` therefore gets German rather than English, which matters:
 * the first venue is Austrian.
 */
export function programmeStringsFor(locale: string | null | undefined): ProgrammeStrings {
  if (!locale) return DEFAULT_PROGRAMME_STRINGS;
  const exact = PROGRAMME_STRINGS[locale.toLowerCase()];
  if (exact) return exact;
  const base = PROGRAMME_STRINGS[locale.toLowerCase().split('-')[0] ?? ''];
  return base ?? DEFAULT_PROGRAMME_STRINGS;
}

export function fillTemplate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => {
    const value = values[key];
    return value === undefined ? match : String(value);
  });
}

// -----------------------------------------------------------------------------
// Formatting
//
// `Intl` formatters are cached: these zones re-render on a clock tick, on a
// Raspberry Pi, for hours on end.
// -----------------------------------------------------------------------------

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  const cached = formatterCache.get(key);
  if (cached) return cached;
  const created = new Intl.DateTimeFormat(locale, options);
  formatterCache.set(key, created);
  return created;
}

const clockConventionCache = new Map<string, boolean>();

/**
 * Whether a locale writes the time on a 24-hour clock.
 *
 * Read from a formatted sample rather than `resolvedOptions().hourCycle`, whose
 * type is not declared in every `lib` the consuming apps compile against.
 */
function usesTwentyFourHourClock(locale: string): boolean {
  const cached = clockConventionCache.get(locale);
  if (cached !== undefined) return cached;
  const sample = formatter(locale, { hour: 'numeric' }).formatToParts(
    new Date(Date.UTC(2020, 0, 1, 15)),
  );
  const result = !sample.some((part) => part.type === 'dayPeriod');
  clockConventionCache.set(locale, result);
  return result;
}

/**
 * Clock time of an instant, in the screen's timezone.
 *
 * The hour is zero-padded on a 24-hour clock and not on a 12-hour one: a German
 * board wants "09:00" in a column of tabular times, while an English one wants
 * "9:00 PM" rather than "09:00 PM".
 */
export function formatTime(
  iso: string,
  payload: ProgrammePayload,
  strings: ProgrammeStrings = DEFAULT_PROGRAMME_STRINGS,
): string {
  const twentyFourHour = strings.timeFormat
    ? strings.timeFormat === 'h23'
    : usesTwentyFourHourClock(payload.locale);
  const options: Intl.DateTimeFormatOptions = {
    hour: twentyFourHour ? '2-digit' : 'numeric',
    minute: '2-digit',
    timeZone: payload.timezone,
  };
  if (strings.timeFormat) options.hourCycle = strings.timeFormat;
  return formatter(payload.locale, options).format(new Date(ms(iso)));
}

/**
 * A programme day's heading: "Today", "Tomorrow", else the weekday and date in
 * the screen's locale.
 *
 * `dayKey` is a civil date, so it is anchored at noon UTC and formatted in UTC.
 * That keeps the label a pure string transformation — no offset is applied to a
 * date that never had a time of day in the first place.
 */
export function formatDayLabel(
  dayKey: string,
  payload: ProgrammePayload,
  strings: ProgrammeStrings = DEFAULT_PROGRAMME_STRINGS,
  todayKey?: string,
): string {
  if (todayKey) {
    if (dayKey === todayKey) return strings.today;
    const todayIndex = payload.days.findIndex((d) => d.dayKey === todayKey);
    if (todayIndex >= 0 && payload.days[todayIndex + 1]?.dayKey === dayKey) {
      return strings.tomorrow;
    }
  }
  return formatter(payload.locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${dayKey}T12:00:00Z`));
}

const displayNamesCache = new Map<string, Intl.DisplayNames | null>();

function languageNames(locale: string): Intl.DisplayNames | null {
  if (!displayNamesCache.has(locale)) {
    try {
      displayNamesCache.set(locale, new Intl.DisplayNames([locale], { type: 'language' }));
    } catch {
      // A runtime without the language display data: fall back to raw codes.
      displayNamesCache.set(locale, null);
    }
  }
  return displayNamesCache.get(locale) ?? null;
}

/**
 * One localised language name, e.g. `en` -> "English" (or "Englisch" for a
 * German screen). Falls back to the upper-cased code, which is what a runtime
 * without the CLDR language data can honestly show.
 */
export function formatLanguage(code: string, locale: string): string {
  const names = languageNames(locale);
  try {
    return names?.of(code) ?? code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
}

/** Localised surtitle language names, e.g. `en, de` -> "English, German". */
export function formatLanguages(codes: readonly string[], locale: string): string {
  return codes.map((code) => formatLanguage(code, locale)).join(', ');
}

/**
 * The surtitle badges for one performance: one badge per language, carrying both
 * the localised name to show and the raw code as a key.
 *
 * A badge per language rather than one "Surtitles: English, German" line,
 * because that is how the information is read at a distance — a viewer scans for
 * their own language rather than reading a sentence (D16).
 */
export function surtitleBadges(
  languages: readonly string[],
  locale: string,
): Array<{ code: string; label: string }> {
  return languages.map((code) => ({ code, label: formatLanguage(code, locale) }));
}
